import React, {
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import cn from 'classnames';

import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { calculateVisibleCount } from '@utils/valueContent/calculateVisibleCount';
import { measureItemWidths } from '@utils/valueContent/measureItemWidths';

import { IValueContentProps } from './ValueContent.types';

import InvisibleItems from './InvisibleItems';
import SelectValue from './SelectValue';

import './ValueContent.scss';

const BUTTON_WIDTH = 50;

const ValueContent: React.FC<IValueContentProps> = ({
	selectedListOfOptions = [],
	containerRef,
	isAllItemsSelected,
	removeAllOptions,
	removeOptions,
	sortFn,
	menuPlacement,
	maxInvisibleItemsHeight,
	columnItemsLength = 3,
	readOnly,
	isColumn,
	isPortal = false,
	isDisabled = false,
}) => {
	const preparedSelectedListOfOptions = useMemo(
		() => selectedListOfOptions.filter(option => option.id),
		[selectedListOfOptions],
	);

	const measureContainerRef = useRef<HTMLDivElement>(null);
	const visibleItemsContainerRef = useRef<HTMLDivElement>(null);
	const invisibleItemsRef = useRef<HTMLDivElement>(null);
	const invisibleItemsWrapperRef = useRef<HTMLDivElement>(null);

	const [itemWidths, setItemWidths] = useState<number[]>([]);
	const [visibleCount, setVisibleCount] = useState<number>(preparedSelectedListOfOptions.length);
	const [isOpenMenuInvisibleItems, setIsOpenMenuInvisibleItems] = useState(false);
	const [isFirstOverflow, setIsFirstOverflow] = useState(false);

	const updateWidths = useCallback(() => {
		setItemWidths(measureItemWidths(measureContainerRef.current));
	}, []);

	useEffect(() => {
		updateWidths();
	}, [preparedSelectedListOfOptions, updateWidths]);

	useLayoutEffect(() => {
		if (!containerRef.current || itemWidths.length === 0) return;

		const count = calculateVisibleCount(containerRef.current, itemWidths, BUTTON_WIDTH);
		const containerWidth = containerRef.current.offsetWidth;

		setVisibleCount(isColumn ? columnItemsLength : count);

		if (count === 1 && itemWidths[0] + BUTTON_WIDTH > containerWidth) {
			setIsFirstOverflow(true);
		} else {
			setIsFirstOverflow(false);
		}
	}, [
		itemWidths,
		columnItemsLength,
		isColumn,
		containerRef?.current?.offsetWidth,
		containerRef,
	]);

	const toggleMenuInvisibleItems = useCallback(() => {
		setIsOpenMenuInvisibleItems(prev => !prev);
	}, []);

	const closeMenuInvisibleItems = useCallback(() => {
		setIsOpenMenuInvisibleItems(false);
	}, []);

	useOnClickOutside({
		refs: [invisibleItemsRef, invisibleItemsWrapperRef],
		handler: () => isOpenMenuInvisibleItems && closeMenuInvisibleItems(),
	});

	const visibleItems = useMemo(() => {
		return preparedSelectedListOfOptions.sort(sortFn).slice(0, isColumn ? columnItemsLength : visibleCount);
	}, [
		isColumn,
		preparedSelectedListOfOptions,
		visibleCount,
		columnItemsLength,
		sortFn,
	]);

	const invisibleItems = useMemo(() => {
		const items = preparedSelectedListOfOptions.sort(sortFn).slice(isColumn ? columnItemsLength : visibleCount);

		if (items.length === 0) {
			closeMenuInvisibleItems();
		}

		return items;
	}, [
		isColumn,
		preparedSelectedListOfOptions,
		visibleCount,
		columnItemsLength,
		closeMenuInvisibleItems,
		sortFn,
	]);

	const wrapperClassName = cn('f5gGroupSelectValueContentWrapper', {
		'f5gGroupSelectValueContentWrapperColumn': isColumn,
	});

	const itemsClassName = cn('f5gGroupSelectVisibleItems', {
		'f5gGroupSelectVisibleItemsColumn': isColumn,
	});

	const invisibleItemsCountClassName = cn('f5gGroupSelectInvisibleItemsCount', {
		'f5gGroupSelectInvisibleItemsCountDisabled': isDisabled,
	});

	return (
		<div
			ref={visibleItemsContainerRef}
			className={wrapperClassName}
		>
			<div
				ref={measureContainerRef}
				className='f5gMeasureContainer'
			>
				{preparedSelectedListOfOptions.map(option => (
					<div
						key={option.id}
						className='f5gMeasureItem'
					>
						<SelectValue
							option={option}
							readOnly={readOnly}
						/>
					</div>
				))}
			</div>
			<div
				className={itemsClassName}
				data-testid='options-container'
			>
				{isAllItemsSelected && !!preparedSelectedListOfOptions.length ? (
					<SelectValue
						option={{
							title: 'All',
							id: 'all-options',
							checked: true,
							categoryName: 'all-options-category',
						}}
						readOnly={readOnly}
						removeHandler={removeAllOptions}
					/>
				) : (
					visibleItems.map(option => (
						<SelectValue
							key={option.id}
							isDisabled={isDisabled}
							isOverflow={visibleItems.length === 1 && isFirstOverflow}
							option={option}
							readOnly={readOnly}
							removeHandler={removeOptions}
						/>
					))
				)}
			</div>
			{(
				(!!invisibleItems.length && !isAllItemsSelected) ||
				(isColumn && !!invisibleItems.length && visibleCount > columnItemsLength)
			) && (
				<div
					ref={invisibleItemsRef}
					className='f5gGroupSelectInvisibleItems'
				>
					<div
						className={invisibleItemsCountClassName}
						onClick={toggleMenuInvisibleItems}
					>
						+{invisibleItems.length}
					</div>
					{isOpenMenuInvisibleItems && (
						<InvisibleItems
							anchorRef={invisibleItemsRef}
							invisibleItems={invisibleItems}
							isDisabled={isDisabled}
							isPortal={isPortal}
							maxHeight={maxInvisibleItemsHeight}
							menuPlacement={menuPlacement}
							removeHandler={removeOptions}
							wrapperRef={invisibleItemsWrapperRef}
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default memo(ValueContent);
